import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaProjectDiagram } from "react-icons/fa";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useDeleteRoutingGraphDeleteMutation, useGetRoutingGraphListQuery } from "../../../openapi";
import { selectGetRoutingGraphsArgs } from "../routing/routingSelectors";
import { FailedAlert } from "../../components/FailedAlert";
import { selectSelectedRoutingGraphId, setSelectedRoutingGraphIdInRedux } from "../designSlice";
import { RoutingGraphSchemaModal } from "../routing/RoutingGraphSchemaModal";

type RoutingGraphRow = {
  id: string;
  systemCode: string;
  systemName: string;
  roomNumber: string;
  floorName: string;
  projectNo: number;
  nodeCount: number;
  edgeCount: number;
};

export const RoutingGraphsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const args = useAppSelector(selectGetRoutingGraphsArgs);
  const selectedRoutingGraphId = useAppSelector(selectSelectedRoutingGraphId);
  const [schemaModalOpened, setSchemaModalOpened] = useState(false);
  const [deleteRoutingGraph, { isLoading: isDeleting }] = useDeleteRoutingGraphDeleteMutation();
  const { data, isLoading, isFetching, isError } = useGetRoutingGraphListQuery(args ?? skipToken);

  const rows = useMemo<RoutingGraphRow[]>(
    () =>
      (data ?? []).map((graph, index) => ({
        id: String(graph._id ?? `graph-${index}`),
        systemCode: graph.systemCode,
        systemName: graph.systemName ?? "",
        roomNumber: graph.roomNumber ?? "",
        floorName: graph.floorName,
        projectNo: graph.projectNo,
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
      })),
    [data]
  );

  if (!args) {
    return (
      <Alert icon={<FaProjectDiagram />} title="Lagrede rør" variant="light">
        Velg prosjekt og etasje for å vise lagrede graphs.
      </Alert>
    );
  }

  if (isLoading || isFetching) {
    return <Loader />;
  }

  if (isError) {
    return <FailedAlert title="Kunne ikke hente lagrede rør" />;
  }

  return (
    <Stack h="100%" gap="sm">
      {schemaModalOpened && <RoutingGraphSchemaModal onClose={() => setSchemaModalOpened(false)} />}

      <Box>
        <Title order={5}>Lagrede rør</Title>
        <Text size="sm" c="dimmed">
          Viser lagrede routing-graphs for valgt etasje.
        </Text>
      </Box>

      <Group justify="flex-end">
        <Button
          size="xs"
          color="red"
          variant="light"
          disabled={!selectedRoutingGraphId || isDeleting}
          loading={isDeleting}
          onClick={() => {
            if (!selectedRoutingGraphId) {
              return;
            }

            void deleteRoutingGraph({ graphId: selectedRoutingGraphId });
          }}
        >
          Slett valgt
        </Button>
      </Group>

      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="sm" pr="xs">
          {rows.length === 0 && (
            <Alert icon={<FaProjectDiagram />} title="Lagrede rør" variant="light">
              Ingen lagrede rør ble funnet for valgt etasje.
            </Alert>
          )}

          {rows.map((row) => {
            const isSelected = selectedRoutingGraphId === row.id;

            return (
              <Card
                key={row.id}
                withBorder
                radius="md"
                padding="sm"
                onClick={() =>
                  dispatch(setSelectedRoutingGraphIdInRedux(isSelected ? null : row.id))
                }
                style={{
                  cursor: "pointer",
                  borderColor: isSelected ? "var(--mantine-color-blue-6)" : undefined,
                  backgroundColor: isSelected ? "var(--mantine-color-blue-0)" : undefined,
                  boxShadow: isSelected ? "0 0 0 1px var(--mantine-color-blue-5) inset" : undefined,
                  transition:
                    "background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                }}
              >
                <Stack gap={8}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text fw={700} size="sm" truncate c={isSelected ? "blue.8" : undefined}>
                        {row.systemCode}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {row.systemName || "Uten systemnavn"}
                      </Text>
                    </Box>
                    <Badge variant={isSelected ? "filled" : "light"}>{row.floorName}</Badge>
                  </Group>

                  <Group gap={6}>
                    <Badge variant="outline">Prosjekt {row.projectNo}</Badge>
                    {row.roomNumber && <Badge variant="outline">Rom {row.roomNumber}</Badge>}
                    {isSelected && (
                      <Badge color="blue" variant="filled">
                        Valgt
                      </Badge>
                    )}
                  </Group>

                  <Group gap={6}>
                    <Badge color="blue" variant="light">
                      {row.nodeCount} noder
                    </Badge>
                    <Badge color="grape" variant="light">
                      {row.edgeCount} lenker
                    </Badge>
                  </Group>

                  {isSelected && (
                    <Group justify="flex-end">
                      <Button
                        size="xs"
                        variant="filled"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSchemaModalOpened(true);
                        }}
                      >
                        Generer flytskjema
                      </Button>
                    </Group>
                  )}

                  <Text size="xs" c="dimmed" style={{ wordBreak: "break-all" }}>
                    {row.id}
                  </Text>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
